import { initFirebase } from '../firebase'
import { collection, getDocs, query, orderBy, limit, doc, getDoc } from 'firebase/firestore'

const { db } = initFirebase()

// Year Ahead Report cost.
//   reportStats/year_ahead  — write-time rollup (count, totalCostUSD, totalMs, …),
//     the source for the headline "avg per report" / "total spent" numbers (one read).
//   report_generations (reportType == 'year_ahead') — one row per generation, with a
//     per-slice cost breakdown. Read directly via the client SDK (ryomen rules allow
//     authed reads; app signs in anonymously).
//
// The collection query is orderBy(createdAt desc) + limit only (single-field,
// auto-indexed) with reportType filtered client-side — a `where(reportType) +
// orderBy(createdAt)` composite index does not exist in this project, and every
// report_generations row today is year_ahead anyway. If a second report type
// appears and dilutes the page, the composite index is a one-click create from
// the link Firestore prints on the failed query.

export const YEAR_AHEAD_SLICES = ['spine', 'overview', 'season_1', 'season_2', 'season_3', 'season_4', 'closing']

function num(v) {
  if (typeof v === 'number') return v
  if (v == null) return 0
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

function toMs(v) {
  if (!v) return 0
  if (typeof v === 'object' && typeof v.toMillis === 'function') return v.toMillis()
  if (typeof v === 'object' && (v.seconds || v._seconds)) return (v.seconds || v._seconds) * 1000
  const t = new Date(v).getTime()
  return isNaN(t) ? 0 : t
}

export class FirebaseYearAheadService {
  // Write-time rollup — the headline numbers, no scan.
  async getStats() {
    const snap = await getDoc(doc(db, 'reportStats', 'year_ahead'))
    if (!snap.exists()) return null
    const x = snap.data()
    return {
      count: num(x.count),
      completed: num(x.completed),
      partial: num(x.partial),
      repairs: num(x.repairs),
      totalCostUSD: num(x.totalCostUSD),
      totalMs: num(x.totalMs),
      totalInputTokens: num(x.totalInputTokens),
      totalOutputTokens: num(x.totalOutputTokens),
      lastAtMs: toMs(x.lastAt)
    }
  }

  // Per-generation rows, newest first. Fetch the newest `max` overall then keep
  // year_ahead client-side (see note above re: composite index).
  async getGenerations(max = 500) {
    const snap = await getDocs(query(
      collection(db, 'report_generations'),
      orderBy('createdAt', 'desc'),
      limit(max)
    ))
    return snap.docs
      .filter(d => d.data().reportType === 'year_ahead')
      .map(d => {
        const x = d.data()
        return {
          id: d.id,
          reportId: x.reportId || '',
          userId: x.userId || '',
          language: x.language || '',
          promptVersion: x.promptVersion || '',
          state: x.state || '',
          partialRepair: x.partialRepair === true,
          totalMs: num(x.totalMs),
          inputTokens: num(x.inputTokens),
          outputTokens: num(x.outputTokens),
          costUsd: num(x.costUsd),
          slices: (x.slices && typeof x.slices === 'object') ? x.slices : {},
          slicesFailed: Array.isArray(x.slicesFailed) ? x.slicesFailed : [],
          createdAtMs: toMs(x.createdAt) || toMs(x.timestamp)
        }
      })
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
  }
}
